// src/components/SupplierLogo.jsx
import React from 'react';

// PLUS D'IMPORTS : Les images sont maintenant dans le dossier public.
// On utilise des chemins absolus (commençant par /)

// Mapping de secours (Fallback) si on n'a pas l'URL directe de la BDD
// J'ai mis à jour les extensions selon votre capture d'écran (.jpeg vs .png)
const SUPPLIER_LOGOS_MAP = {
  'ENGIE': '/logo_fournisseur/Engie.png',
  'ENI': '/logo_fournisseur/Eni.png',
  'GAZEL ENERGIE': '/logo_fournisseur/GazelEnergie.png',
  'GAZEL': '/logo_fournisseur/GazelEnergie.png',
  'LA BELLE ENERGIE': '/logo_fournisseur/la Belle Energie.png',
  'LABELL': '/logo_fournisseur/la Belle Energie.png',
  'OHM ENERGIE': '/logo_fournisseur/Ohm.png',
  'OHM': '/logo_fournisseur/Ohm.png',
  'PICOTY': '/logo_fournisseur/Picoty.png',
  'PRIMEO ENERGIE': '/logo_fournisseur/Primeo.png',
  'PRIMEO': '/logo_fournisseur/Primeo.png',
  'TOTALENERGIES': '/logo_fournisseur/Total.png',
  'TOTAL': '/logo_fournisseur/Total.png',
  'BUTAGAZ': '/logo_fournisseur/butagaz.jpeg', // Attention .jpeg
  'GEG': '/logo_fournisseur/Geg.jpeg', // Attention .jpeg
  'ANTARGAZ': '/logo_fournisseur/antargaz.png', // Minuscule
  'EDF': '/logo_fournisseur/edf.png', // Minuscule
  'SOWEE': '/logo_fournisseur/sowee.png',
  'VATTENFALL': '/logo_fournisseur/vattenfall.png',
  'ILEK': '/logo_fournisseur/ilek.png',
  'EKWATEUR': '/logo_fournisseur/ekwateur.png',
  'MINT ENERGIE': '/logo_fournisseur/mint_energy.png',
  'ALPIQ': '/logo_fournisseur/alpiq.png',
  'ALTERNA': '/logo_fournisseur/alterna.png',
  'DYNEFF': '/logo_fournisseur/dyneff.png',
  'ELECOCITE': '/logo_fournisseur/elecocite.png',
  'ELMY': '/logo_fournisseur/elmy.png',
  'ENERCOOP': '/logo_fournisseur/enercoop.png',
  'ENERGIE D\'ICI': '/logo_fournisseur/energie_d_ici.jpeg',
  'ENERGIES DU SANTERRE': '/logo_fournisseur/energies_du_santerre.png',
  'GAZ DE BORDEAUX': '/logo_fournisseur/gaz_de_bordeaux.png',
  'HAPP-E': '/logo_fournisseur/happ-e.png',
  'OCTOPUS ENERGY': '/logo_fournisseur/octopus_energy.png',
  'PAPERNEST': '/logo_fournisseur/papernest_energie.png',
  'PROXELIA': '/logo_fournisseur/Proxelia.jpeg',
  'URBAN SOLAR': '/logo_fournisseur/urban_solar.png',
  'WEKIWI': '/logo_fournisseur/wekiwi.png',
  'YELI': '/logo_fournisseur/Yeli.png'
};

/**
 * Trouve le logo correspondant au nom du fournisseur
 */
const findSupplierLogoPath = (supplierName) => {
  if (!supplierName) return null;

  const normalizedName = supplierName.toUpperCase().trim();

  // 1. Recherche exacte dans le mapping
  if (SUPPLIER_LOGOS_MAP[normalizedName]) {
    return SUPPLIER_LOGOS_MAP[normalizedName];
  }

  // 2. Recherche par inclusion (ex: "ENGIE PRO" trouve "ENGIE")
  for (const [key, path] of Object.entries(SUPPLIER_LOGOS_MAP)) {
    if (normalizedName.includes(key)) {
      return path;
    }
  }

  return null;
};

/**
 * Composant d'affichage du logo fournisseur
 * 
 * @param {string} supplierName - Nom du fournisseur (Ex: "Engie")
 * @param {string} logoUrl - (Optionnel) URL directe venant de la BDD (Ex: "/logo_fournisseur/Engie.png")
 * @param {string} size - Taille : 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * @param {string} className - Classes CSS additionnelles
 * @param {boolean} showFallback - Afficher le nom si logo non trouvé
 */
const SupplierLogo = ({ 
  supplierName, 
  logoUrl = null, 
  size = 'md', 
  className = '', 
  showFallback = true 
}) => {
  // Priorité 1 : URL passée en prop (celle de la BDD)
  // Priorité 2 : Recherche dans le mapping local via le nom
  const logoSrc = logoUrl || findSupplierLogoPath(supplierName);

  // Tailles prédéfinies
  const sizeClasses = {
    xs: 'h-4 w-auto',
    sm: 'h-6 w-auto',
    md: 'h-8 w-auto',
    lg: 'h-10 w-auto',
    xl: 'h-12 w-auto',
  };

  const sizeClass = sizeClasses[size] || sizeClasses.md;

  // Gestion de l'erreur d'image (si le fichier n'existe pas malgré le mapping)
  const handleImageError = (e) => {
    e.target.style.display = 'none'; // Cache l'image cassée
    // Si on voulait afficher le texte à la place, il faudrait gérer un état local,
    // mais pour faire simple, on cache juste l'image.
  };

  if (logoSrc) {
    return (
      <img
        src={logoSrc}
        alt={`Logo ${supplierName}`}
        className={`${sizeClass} object-contain ${className}`}
        title={supplierName}
        onError={handleImageError}
      />
    );
  }

  // Fallback textuel
  if (showFallback && supplierName) {
    return (
      <div className={`${sizeClass} flex items-center justify-center bg-gray-50 rounded px-2 ${className}`}>
        <span className="text-xs font-medium text-gray-700 truncate">
          {supplierName}
        </span>
      </div>
    );
  }

  return null;
};

export default SupplierLogo;